import { GlobalOutlined } from '@ant-design/icons';
import { Select, Tooltip } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled, { createGlobalStyle } from 'styled-components';

import { type SupportedLanguage, languageNames, supportedLanguages } from 'utils/i18n';

const DROPDOWN_CLASSNAME = 'language-switcher-dropdown';

// Colors come from the theme (per AGENTS.md) so the switcher renders correctly in
// both light and dark. The previous implementation used undefined CSS variables
// (`var(--bg-secondary, white)`) whose white/black fallbacks always won, breaking
// dark mode.
const LanguageSelector = styled(Select)`
  min-width: 120px;

  .ant-select-selector {
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.borderColor} !important;
    background-color: ${({ theme }) => theme.secondaryBackgroundColor} !important;

    .ant-select-selection-item {
      color: ${({ theme }) => theme.textColor} !important;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  &:hover .ant-select-selector {
    border-color: ${({ theme }) => theme.teal} !important;
  }
`;

// antd portals the dropdown to <body>, outside the styled(Select) subtree — so a
// nested `.ant-select-dropdown` rule never applied. A global style (rendered inside
// the ThemeProvider) themes the portaled popup by its className instead.
const DropdownGlobalStyle = createGlobalStyle`
  .${DROPDOWN_CLASSNAME} {
    background-color: ${({ theme }) => theme.secondaryBackgroundColor} !important;
    border: 1px solid ${({ theme }) => theme.borderColor} !important;
    box-shadow:
      0 3px 6px -4px rgba(0, 0, 0, 0.12),
      0 6px 16px 0 rgba(0, 0, 0, 0.08);

    .ant-select-item {
      color: ${({ theme }) => theme.textColor};
    }

    .ant-select-item-option-active:not(.ant-select-item-option-disabled),
    .ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
      background-color: ${({ theme }) => theme.selectedListItemBackground};
      color: ${({ theme }) => theme.textColor};
    }
  }
`;

interface LanguageSwitcherProps {
  /** Size of the component */
  size?: 'small' | 'middle' | 'large';
  /** Whether to show tooltip */
  showTooltip?: boolean;
  /** Additional CSS class */
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  size = 'middle',
  showTooltip = true,
  className,
}) => {
  const { i18n, t } = useTranslation();

  const handleLanguageChange = (language: SupportedLanguage) => {
    // Persist the selection, then reload. LanguageDetector reads this key on
    // init, so after the reload the whole app — including translations that are
    // evaluated at module-load time (column/tab definitions in utils/constants
    // and data/*) — comes back fully in the chosen language. Without the reload
    // those module-level lookups keep the language captured at first import.
    localStorage.setItem('skyspecs-language', language);
    i18n.changeLanguage(language);
    window.location.reload();
  };

  const selector = (
    <LanguageSelector
      data-testid="language-switcher"
      value={i18n.language as SupportedLanguage}
      onChange={value => handleLanguageChange(value as SupportedLanguage)}
      size={size}
      className={className}
      dropdownClassName={DROPDOWN_CLASSNAME}
      dropdownMatchSelectWidth={false}
      suffixIcon={null} // Remove default dropdown arrow as we have the globe icon
      // Use the options prop rather than styled <Select.Option> children: a
      // styled() Option is not recognized by antd as a real Option, so its
      // value never reaches onChange (selection silently no-ops in the browser).
      options={supportedLanguages.map(lang => ({
        value: lang,
        label: (
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <GlobalOutlined />
            {languageNames[lang]}
          </span>
        ),
      }))}
    />
  );

  if (showTooltip) {
    return (
      <>
        <DropdownGlobalStyle />
        <Tooltip title={t('common.selectLanguage', 'Select Language')} placement="bottom">
          {selector}
        </Tooltip>
      </>
    );
  }

  return (
    <>
      <DropdownGlobalStyle />
      {selector}
    </>
  );
};

export default LanguageSwitcher;
